import { ECardinality, type IActionDefinitionDto, type IComponent, type IKTypeField } from "@kuika/kuika-cl-model";
import type { IManagedBindingSource } from "./managed-screen-binding-sources";

/**
 * KOA-18136 — everything on THIS screen that an action parameter can be filled
 * from, in one list.
 *
 * ## Why one list
 *
 * A parameter is filled by what it IS. `ProcessInstanceId` is the running
 * process; `ExpenseCategoryId` is whatever the user picked in the select box
 * next to the table; a detail id is a field of the row a previous read returned.
 * Deciding that requires all three kinds of value on the table AT THE SAME TIME
 * — there is no order of preference between them, and a caller that can only
 * see one kind does not resolve the question conservatively, it resolves it
 * wrongly.
 *
 * That is what shipped: the inventory offered only the process values, so the
 * repair pass filling a wizard task screen had nothing else to reach for. On the
 * ordinary screen built in the same run the same action was filled correctly —
 * its filter bound to the select box, its user column to the current user —
 * because the deterministic Binder that runs there does see the screen's own
 * components. The managed screen had no such pass and no such inventory, and its
 * `ExpenseCategoryId` went out empty (live 2026-08-12).
 *
 * ## Why here and not on the server
 *
 * Every fact needed to answer "can this hold a value, and how do I read it" is
 * designer state:
 *
 *   - whether a component holds a value at all is `isValueProp` on the component
 *     DEFINITION (the same field the parameter-affinity guard uses), and which
 *     property to read is that property's name — `value`, `checked`,
 *     `selectedDate`. Guessing it from a component-info dump gets Labels wrong
 *     (a Label has a `value` and holds nothing a user chose);
 *   - an action's result fields are its definition's `returnTypeModel`, and the
 *     id a binding names is the action INSTANCE on this screen, not its
 *     definition — only the screen's own display items carry that pairing.
 *
 * Each entry therefore ships with the exact payload
 * `ui_action.set_multiple_parameter_bindings` takes, so a caller matches names
 * and dispatches; it never has to know how a family is addressed.
 */

/** Which family a source belongs to. Reported so a caller can EXPLAIN a choice. */
export type ScreenBindingSourceOrigin = "process" | "component" | "action-result";

/** The dispatch payload for one source — the fields its `bindingType` requires. */
export interface IScreenBindingTarget {
  readonly bindingType: "processParameter" | "componentValue" | "actionField";
  /** processParameter — the platform keyword / workflow parameter id. */
  readonly inputName?: string;
  /** componentValue — the component holding the value, and which slot to read. */
  readonly sourceKuikaId?: string;
  readonly sourcePropertyName?: string;
  /** actionField — the action INSTANCE on this screen, and the field of its result. */
  readonly sourceActionId?: string;
  readonly fieldName?: string;
}

export interface IScreenBindingSource {
  /** Stable within one inventory; what a model names when it picks this source. */
  readonly id: string;
  /** Human-facing, and specific enough to choose between two of the same family. */
  readonly label: string;
  readonly origin: ScreenBindingSourceOrigin;
  /** Backend value type when known — `""` when the screen does not declare one. */
  readonly type: string;
  /** Process sub-family (`process-instance` | `business-object` | `internal`). */
  readonly kind?: string;
  /**
   * The names a caller may match a parameter against.
   *
   * Deliberately QUALIFIED for action results (`ListCategories.Id`, never a bare
   * `Id`): a field name on its own does not prove what it means, and a
   * deterministic match on `Id` would bind the first list that happens to have
   * one. Bare field names still reach a model through `label` — that is a
   * judgement, and judgements are not made by lookup order.
   */
  readonly names: readonly string[];
  readonly binding: IScreenBindingTarget;
}

/** Structural subset of a display item this module walks. */
export interface IInventoryDisplayItem {
  id?: number | string;
  friendlyName?: string | null;
  item?: {
    id?: string;
    name?: string;
    properties?: { name?: string; value?: unknown; isValueProp?: boolean }[];
    events?: { eventName?: string; actions?: IInventoryAction[] }[];
  };
  children?: IInventoryDisplayItem[];
}

export interface IInventoryAction {
  id?: string;
  label?: string;
  description?: string;
  actionDefinitionId?: string;
  returnCardinality?: ECardinality;
  ifThenElseData?: { ifActions?: IInventoryAction[]; elseActions?: IInventoryAction[] };
}

export interface IScreenBindingInventoryInput {
  /** The process values this screen carries — empty on an ordinary screen. */
  readonly processSources: readonly IManagedBindingSource[];
  readonly displayItems: readonly IInventoryDisplayItem[];
  readonly componentDefinitions: readonly IComponent[];
  readonly actionDefinitions: readonly IActionDefinitionDto[];
}

/**
 * Upper bound on the entries one inventory returns.
 *
 * A screen with a dozen reads of wide result types can produce hundreds of
 * fields, and every entry is read by a model. The cap keeps the offer legible;
 * `truncated` says it happened rather than presenting a short list as the whole
 * truth — a silently trimmed inventory reads exactly like a screen that carries
 * nothing, which is the failure mode this whole module exists to end.
 */
export const SCREEN_BINDING_SOURCE_LIMIT: number = 150;

export interface IScreenBindingInventory {
  readonly sources: IScreenBindingSource[];
  readonly truncated: boolean;
}

/**
 * The screen's whole offer: its process values, its value-holding components,
 * and the result fields of the actions attached to it.
 *
 * Ordering is presentation only — process values first because they are the ones
 * matched by name without judgement, then the screen's own controls, then action
 * results. No consumer may read the order as precedence.
 */
export function buildScreenBindingInventory(input: IScreenBindingInventoryInput): IScreenBindingInventory {
  const sources: IScreenBindingSource[] = [
    ...processBindingSources(input.processSources),
    ...componentBindingSources(input.displayItems, input.componentDefinitions),
    ...actionResultBindingSources(input.displayItems, input.actionDefinitions)
  ];
  if (sources.length <= SCREEN_BINDING_SOURCE_LIMIT) return { sources, truncated: false };
  return { sources: sources.slice(0, SCREEN_BINDING_SOURCE_LIMIT), truncated: true };
}

function processBindingSources(processSources: readonly IManagedBindingSource[]): IScreenBindingSource[] {
  return processSources.map((source: IManagedBindingSource): IScreenBindingSource => ({
    id: `process:${source.key}`,
    label: source.label,
    origin: "process",
    type: source.type,
    kind: source.kind,
    // Both spellings are load-bearing: generated SQL signatures use the picker
    // label (`ProcessInstanceId`), while a workflow parameter is matched by the
    // analyst's own name and addressed by an id nobody types.
    names: uniqueNames([source.label, source.key]),
    binding: { bindingType: "processParameter", inputName: source.key }
  }));
}

/**
 * Every component on the screen that HOLDS a value, with the slot to read it
 * from.
 *
 * `isValueProp` on the component definition is the platform's own answer to
 * "does a user's choice live here" — the same field the binding affinity guard
 * trusts. A Label carries a `value` property and holds nothing chosen, so a
 * property-name heuristic would offer it; the definition does not.
 */
function componentBindingSources(
  displayItems: readonly IInventoryDisplayItem[],
  componentDefinitions: readonly IComponent[]
): IScreenBindingSource[] {
  const out: IScreenBindingSource[] = [];
  walkDisplayItems(displayItems, (di: IInventoryDisplayItem): void => {
    const kuikaId: string = String(di.id ?? "").trim();
    if (kuikaId.length === 0) return;
    const definition: IComponent | undefined = componentDefinitions.find(
      (component: IComponent): boolean => component.id === di.item?.id
    );
    const valueProperty = (definition?.properties ?? []).find((property): boolean => property.isValueProp === true);
    if (valueProperty === undefined || typeof valueProperty.name !== "string" || valueProperty.name.length === 0) {
      return;
    }
    const componentType: string = di.item?.name ?? "";
    const friendlyName: string = (di.friendlyName ?? "").trim();
    const caption: string = readStringProperty(di, "label") ?? readStringProperty(di, "placeholder") ?? "";
    const label: string =
      friendlyName.length > 0 ? `${friendlyName} (${componentType})` : `${componentType} ${kuikaId}`;
    out.push({
      id: `component:${kuikaId}`,
      label: caption.length > 0 ? `${label} — "${caption}"` : label,
      origin: "component",
      type: "",
      names: uniqueNames([friendlyName, caption]),
      binding: {
        bindingType: "componentValue",
        sourceKuikaId: kuikaId,
        sourcePropertyName: valueProperty.name
      }
    });
  });
  return out;
}

/**
 * Every field of every action result available on the screen.
 *
 * The id a binding names is the action INSTANCE (`action.id`), because that is
 * what the symbol resolver looks up — a definition id resolves to nothing and
 * the binding lands dead. A result with many rows still exposes its fields: the
 * platform reads them as the first row (`[Action].first.Field`), which the label
 * says out loud so a caller choosing one knows what it will get.
 */
function actionResultBindingSources(
  displayItems: readonly IInventoryDisplayItem[],
  actionDefinitions: readonly IActionDefinitionDto[]
): IScreenBindingSource[] {
  const out: IScreenBindingSource[] = [];
  const seen: Set<string> = new Set<string>();
  walkDisplayItems(displayItems, (di: IInventoryDisplayItem): void => {
    for (const event of di.item?.events ?? []) {
      walkActions(event.actions ?? [], (action: IInventoryAction): void => {
        const actionId: string = (action.id ?? "").trim();
        if (actionId.length === 0) return;
        const definition: IActionDefinitionDto | undefined = actionDefinitions.find(
          (candidate: IActionDefinitionDto): boolean => candidate.id === action.actionDefinitionId
        );
        const fields: IKTypeField[] = definition?.returnTypeModel?.elements ?? [];
        if (fields.length === 0) return;
        const actionName: string = definition?.name ?? action.label ?? actionId;
        const many: boolean = action.returnCardinality === ECardinality.Many;
        for (const field of fields) {
          const fieldName: string = (field?.name ?? "").trim();
          if (fieldName.length === 0) continue;
          const id: string = `action:${actionId}:${fieldName}`;
          if (seen.has(id)) continue;
          seen.add(id);
          out.push({
            id,
            label: many ? `${actionName} → first row's ${fieldName}` : `${actionName} → ${fieldName}`,
            origin: "action-result",
            type: field.type?.name ?? "",
            names: uniqueNames([`${actionName}.${fieldName}`, `${actionName}${fieldName}`]),
            binding: { bindingType: "actionField", sourceActionId: actionId, fieldName }
          });
        }
      });
    }
  });
  return out;
}

function readStringProperty(di: IInventoryDisplayItem, propertyName: string): string | undefined {
  const property = (di.item?.properties ?? []).find((candidate): boolean => candidate.name === propertyName);
  const value: unknown = property?.value;
  if (typeof value !== "string") return undefined;
  const trimmed: string = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function walkDisplayItems(items: readonly IInventoryDisplayItem[], visit: (di: IInventoryDisplayItem) => void): void {
  for (const di of items) {
    if (di === null || di === undefined) continue;
    visit(di);
    walkDisplayItems(di.children ?? [], visit);
  }
}

/** Conditional branches hold real actions too — their results are just as bindable. */
function walkActions(actions: readonly IInventoryAction[], visit: (action: IInventoryAction) => void): void {
  for (const action of actions) {
    if (action === null || action === undefined) continue;
    visit(action);
    walkActions(action.ifThenElseData?.ifActions ?? [], visit);
    walkActions(action.ifThenElseData?.elseActions ?? [], visit);
  }
}

function uniqueNames(candidates: readonly string[]): string[] {
  const out: string[] = [];
  const seen: Set<string> = new Set<string>();
  for (const candidate of candidates) {
    const trimmed: string = (candidate ?? "").trim();
    if (trimmed.length === 0 || seen.has(trimmed)) continue;
    seen.add(trimmed);
    out.push(trimmed);
  }
  return out;
}
